// One versioned key for everything this browser remembers (IMPLEMENTATION_PLAN.md §20).
//
// Every read and every write goes through this module. Scattered localStorage calls are how the
// theme and the per-model parameters ended up with two shapes, two prefixes and two different
// failure modes; one key with one parser has one of each.
//
// Storage is never trusted. A person can edit it, an older build can have written a different
// shape, and JSON.parse throws on anything that is not JSON at all — and a throw on the first
// render white-screens the whole application. So every field is coerced and anything
// unrecognised falls back to its default.

import { useSyncExternalStore } from "react";
import type { ParameterValues } from "../api";

const KEY = "darkroom.prefs.v1";

/** Where the first pass kept the theme. Read once, so the choice survives the upgrade. */
const LEGACY_THEME_KEY = "darkroom.theme";

export type ThemeChoice = "dark" | "light" | "system";

/** How a board is laid out: the full stream of cards, or just the images as tiles. */
export type BoardView = "stream" | "tiles";

export interface Preferences {
  theme: ThemeChoice;
  /** Null until one has been chosen, never an empty string. */
  last_model_id: string | null;
  /**
   * Models that have actually produced a generation, most recent first, no duplicates. The picker
   * shows the first few; the list is kept a little longer so a model withdrawn from the catalog
   * does not leave a gap.
   */
  recent_model_ids: string[];
  last_board_id: string | null;
  /** Contact sheet density. Applied only where the grid is wide enough to honour it. */
  library_columns: number;
  /** Studio layout. Tiles hide the prompt, ordinals and readings so more images fit on screen. */
  board_view: BoardView;
  parameters_by_model: Record<string, ParameterValues>;
}

export const LIBRARY_COLUMN_CHOICES = [3, 4, 5, 6];

/** How many models the picker surfaces as recently used. */
export const RECENT_MODELS_SHOWN = 3;

/** How many are remembered, so the shown three survive a model leaving the catalog. */
const RECENT_MODELS_KEPT = 8;

const DEFAULTS: Preferences = {
  theme: "system",
  last_model_id: null,
  recent_model_ids: [],
  last_board_id: null,
  library_columns: 5,
  board_view: "stream",
  parameters_by_model: {},
};

// ── Coercion ───────────────────────────────────────────────────────────────────────────────

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "dark" || value === "light" || value === "system";
}

function isBoardView(value: unknown): value is BoardView {
  return value === "stream" || value === "tiles";
}

function coerceText(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/** Non-empty strings only, first occurrence wins, capped. Anything else in the array is dropped. */
function coerceRecentModels(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  const ids: string[] = [];
  for (const entry of value) {
    if (typeof entry === "string" && entry !== "" && !ids.includes(entry)) ids.push(entry);
    if (ids.length === RECENT_MODELS_KEPT) break;
  }
  return ids;
}

function coerceColumns(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULTS.library_columns;
  const rounded = Math.round(value);
  return LIBRARY_COLUMN_CHOICES.includes(rounded) ? rounded : DEFAULTS.library_columns;
}

/** Only scalars survive: a control cannot render an object, and the server would reject one. */
function coerceValues(value: unknown): ParameterValues | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;

  const values: ParameterValues = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "string" || typeof entry === "number" || typeof entry === "boolean") {
      values[key] = entry;
    }
  }
  return values;
}

function coerceParametersByModel(value: unknown): Record<string, ParameterValues> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};

  const byModel: Record<string, ParameterValues> = {};
  for (const [modelId, entry] of Object.entries(value as Record<string, unknown>)) {
    const values = coerceValues(entry);
    if (values) byModel[modelId] = values;
  }
  return byModel;
}

function coerce(raw: unknown): Preferences {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return DEFAULTS;
  const stored = raw as Record<string, unknown>;

  return {
    theme: isThemeChoice(stored.theme) ? stored.theme : DEFAULTS.theme,
    last_model_id: coerceText(stored.last_model_id),
    recent_model_ids: coerceRecentModels(stored.recent_model_ids),
    last_board_id: coerceText(stored.last_board_id),
    library_columns: coerceColumns(stored.library_columns),
    board_view: isBoardView(stored.board_view) ? stored.board_view : DEFAULTS.board_view,
    parameters_by_model: coerceParametersByModel(stored.parameters_by_model),
  };
}

function load(): Preferences {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw !== null) return coerce(JSON.parse(raw) as unknown);

    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    return isThemeChoice(legacy) ? { ...DEFAULTS, theme: legacy } : DEFAULTS;
  } catch {
    // Storage can be unavailable outright (private mode, blocked cookies). Defaults still work.
    return DEFAULTS;
  }
}

// ── Store ──────────────────────────────────────────────────────────────────────────────────

// A module-level store rather than a context: the composer writes the model it just used and the
// masthead reads the theme, and threading a provider through the tree for that is more machinery
// than the problem deserves. useSyncExternalStore is React's own answer to exactly this shape.

let current: Preferences = load();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): Preferences {
  return current;
}

export type PreferencesPatch =
  | Partial<Preferences>
  | ((previous: Preferences) => Partial<Preferences>);

/** The single writer. Merges, persists, then tells every subscriber. */
export function setPreferences(patch: PreferencesPatch): void {
  const changes = typeof patch === "function" ? patch(current) : patch;
  current = { ...current, ...changes };

  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // Forgetting a preference is not worth interrupting anyone over; the session still has it.
  }

  for (const listener of listeners) listener();
}

/** A model has just been used to make something: move it to the front of the recent list. */
export function recordModelUse(modelId: string): void {
  if (modelId === "") return;
  setPreferences((previous) => ({
    recent_model_ids: [
      modelId,
      ...previous.recent_model_ids.filter((id) => id !== modelId),
    ].slice(0, RECENT_MODELS_KEPT),
  }));
}

/**
 * The current preferences outside a render — for effects that must not re-run merely because a
 * preference changed. Components use the hook.
 */
export function readPreferences(): Preferences {
  return current;
}

export function usePreferences(): [Preferences, (patch: PreferencesPatch) => void] {
  const preferences = useSyncExternalStore(subscribe, snapshot);
  return [preferences, setPreferences];
}
